import { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog';
import { Save } from 'lucide-react';
import { LoyaltyWidget } from '../components/LoyaltyWidget';

// Import settings section components
import {
  TeamIdentitySection,
  AppearanceSection,
  NavigationSection,
  AccountInfoSection,
  PreviewSection,
  SessionsSection,
  SecuritySection,
  MatchStatsSection,
  MatchDefaultsSection,
  OpponentsCompetitionsSection,
  CupProgressLabelsSection,
  PlayerCardStatsSection,
  MatchDayModeSection,
  DrillPresetsSection,
  ReferralSection,
  SubscriptionSection,
  DataPrivacySection,
  PublicProfileSection,
  DEFAULT_MENU_COLOR,
  DEFAULT_BUTTON_COLOR,
  DEFAULT_PAGE_ORDER,
  mergePageOrder
} from '../components/settings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user, updateUser, logout, currentSeasonName } = useAuth();
  const navigate = useNavigate();
  
  // Team Identity state
  const [teamName, setTeamName] = useState(user?.team_name || '');
  const [clubBadge, setClubBadge] = useState(user?.club_badge || '');
  const [badgePreview, setBadgePreview] = useState(user?.club_badge || '');
  const [headerImage, setHeaderImage] = useState(user?.header_image || '');
  const [headerPreview, setHeaderPreview] = useState(user?.header_image || '');
  const [dressingRoomHeader, setDressingRoomHeader] = useState(user?.dressing_room_header_image || '');
  const [dressingRoomHeaderPreview, setDressingRoomHeaderPreview] = useState(user?.dressing_room_header_image || '');
  const [tickerEnabled, setTickerEnabled] = useState(user?.ticker_enabled || false);
  const [tickerMode, setTickerMode] = useState(user?.ticker_mode || 'manual');
  const [tickerLabel, setTickerLabel] = useState(user?.ticker_label || 'BREAKING NEWS:');
  const [tickerText, setTickerText] = useState(user?.ticker_text || '');
  const [currentRole, setCurrentRole] = useState(user?.current_role || '');
  // Profile photo (personal — Account Settings, not per-season)
  const [profilePhoto, setProfilePhoto] = useState(user?.profile_photo || '');
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(user?.profile_photo || '');
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  
  // Appearance state
  const [menuColor, setMenuColor] = useState(user?.menu_color || DEFAULT_MENU_COLOR);
  const [buttonColor, setButtonColor] = useState(user?.button_color || DEFAULT_BUTTON_COLOR);
  const [sidebarBgImage, setSidebarBgImage] = useState(user?.sidebar_bg_image || '');
  const [sidebarBgOpacity, setSidebarBgOpacity] = useState(user?.sidebar_bg_opacity ?? 0.3);
  const [sidebarBgPreview, setSidebarBgPreview] = useState(user?.sidebar_bg_image || '');
  const [sidebarBgFile, setSidebarBgFile] = useState(null);
  const [sidebarBgOffset, setSidebarBgOffset] = useState(user?.sidebar_bg_offset ?? 0);
  
  // Navigation state
  const [hiddenPages, setHiddenPages] = useState(user?.hidden_pages || []);
  const [pageOrder, setPageOrder] = useState(() => mergePageOrder(user?.page_order, DEFAULT_PAGE_ORDER));
  const [draggedPage, setDraggedPage] = useState(null);
  const [dragOverPage, setDragOverPage] = useState(null);
  
  // Security state
  const [securityEmailsEnabled, setSecurityEmailsEnabled] = useState(user?.security_emails_enabled !== false);
  
  // Match Stats state
  const [timelineAutoStats, setTimelineAutoStats] = useState(user?.timeline_auto_stats || false);
  
  // Match Defaults state
  const [preferredFormation, setPreferredFormation] = useState(user?.preferred_formation || '');
  const [defaultKickOffTime, setDefaultKickOffTime] = useState(user?.default_kick_off_time || '');
  const [halfLength, setHalfLength] = useState(user?.half_length || 45);
  const [extraTimeHalfLength, setExtraTimeHalfLength] = useState(user?.extra_time_half_length || 15);
  const DEFAULT_PRESETS = ['Injured', 'Suspended', 'Dropped', 'Holiday', 'Work', 'Unavailable'];
  const [presetReasons, setPresetReasons] = useState(user?.preset_non_selection_reasons || DEFAULT_PRESETS);
  
  // Match Day Mode state
  const [matchDayModeEnabled, setMatchDayModeEnabled] = useState(user?.match_day_mode_enabled !== false);
  const [voiceButtonPosition, setVoiceButtonPosition] = useState(user?.voice_button_position || 'right');
  const [voiceControlEnabled, setVoiceControlEnabled] = useState(user?.voice_control_enabled !== false);
  const [voiceEngine, setVoiceEngine] = useState(user?.voice_engine || 'webspeech');
  
  // Opponents display
  const [showOpponentBadges, setShowOpponentBadges] = useState(user?.show_opponent_badges !== false);
  const [countdownTimerEnabled, setCountdownTimerEnabled] = useState(user?.countdown_timer_enabled !== false);
  const [noteCategories, setNoteCategories] = useState(user?.coach_note_categories || ['Shape', 'Defence', 'Attack', 'Set Pieces', 'Formation']);
  const [cupProgressLabels, setCupProgressLabels] = useState(user?.cup_progress_labels || {});
  const [playerCardStats, setPlayerCardStats] = useState(user?.player_card_stats || []);
  
  // Drill Presets state
  const [drillPresets, setDrillPresets] = useState(user?.drill_presets || []);
  
  // File upload state
  const [badgeFile, setBadgeFile] = useState(null);
  const [headerFile, setHeaderFile] = useState(null);
  const [dressingRoomHeaderFile, setDressingRoomHeaderFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(true);
  
  // Refs
  const displayNameRef = useRef(null);
  const personalBioRef = useRef(null);
  const coachQualificationsRef = useRef(null);
  const saveButtonRef = useRef(null);

  // Track save button visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFloatingButton(!entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (saveButtonRef.current) {
      observer.observe(saveButtonRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Resync per-season form fields when the coach switches season (or clears it).
  // `user` in AuthContext is refreshed via `/auth/me` after a season switch, with
  // `apply_season_overrides` overlaying the new season's `settings` on top of
  // the account-level user doc. Without this effect, the local `useState`
  // initialisers (which only run at mount) keep showing the previous season's
  // team name, badge, ticker, match defaults, etc. even though the sidebar +
  // top bar update correctly.
  //
  // Scope is deliberately limited to fields listed in `SEASON_OVERRIDABLE_FIELDS`
  // on the backend (plus badge/header previews that follow `club_badge` /
  // `header_image`). Account-level fields (menu color, page order, sessions,
  // etc.) are intentionally left alone so a coach editing appearance doesn't
  // lose in-progress work when they switch season from the top bar.
  useEffect(() => {
    setTeamName(user?.team_name || '');
    setClubBadge(user?.club_badge || '');
    setBadgePreview(user?.club_badge || '');
    setBadgeFile(null);
    setHeaderImage(user?.header_image || '');
    setHeaderPreview(user?.header_image || '');
    setHeaderFile(null);
    setDressingRoomHeader(user?.dressing_room_header_image || '');
    setDressingRoomHeaderPreview(user?.dressing_room_header_image || '');
    setDressingRoomHeaderFile(null);
    setTickerEnabled(user?.ticker_enabled || false);
    setTickerMode(user?.ticker_mode || 'manual');
    setTickerLabel(user?.ticker_label || 'BREAKING NEWS:');
    setTickerText(user?.ticker_text || '');
    setCurrentRole(user?.current_role || '');
    setTimelineAutoStats(user?.timeline_auto_stats || false);
    setPreferredFormation(user?.preferred_formation || '');
    setDefaultKickOffTime(user?.default_kick_off_time || '');
    setHalfLength(user?.half_length || 45);
    setExtraTimeHalfLength(user?.extra_time_half_length || 15);
    setPresetReasons(user?.preset_non_selection_reasons || DEFAULT_PRESETS);
    setMatchDayModeEnabled(user?.match_day_mode_enabled !== false);
    setVoiceButtonPosition(user?.voice_button_position || 'right');
    setVoiceControlEnabled(user?.voice_control_enabled !== false);
    setVoiceEngine(user?.voice_engine || 'webspeech');
    setShowOpponentBadges(user?.show_opponent_badges !== false);
    setCountdownTimerEnabled(user?.countdown_timer_enabled !== false);
    setNoteCategories(user?.coach_note_categories || ['Shape', 'Defence', 'Attack', 'Set Pieces', 'Formation']);
    setCupProgressLabels(user?.cup_progress_labels || {});
    setPlayerCardStats(user?.player_card_stats || []);
    setDrillPresets(user?.drill_presets || []);
    // Profile photo lives on the account (not per-season) — resync only if
    // its current value differs, so we don't clobber an unsaved upload.
    if (user?.profile_photo !== profilePhoto) {
      setProfilePhoto(user?.profile_photo || '');
      setProfilePhotoPreview(user?.profile_photo || '');
      setProfilePhotoFile(null);
    }
  }, [user?.current_season_id]);

  // Memoize JSON stringified values to avoid recalculating on every render
  const userHiddenPagesJson = useMemo(() => JSON.stringify(user?.hidden_pages || []), [user?.hidden_pages]);
  const userPageOrderJson = useMemo(() => JSON.stringify(mergePageOrder(user?.page_order, DEFAULT_PAGE_ORDER)), [user?.page_order]);
  const hiddenPagesJson = useMemo(() => JSON.stringify(hiddenPages), [hiddenPages]);
  const pageOrderJson = useMemo(() => JSON.stringify(pageOrder), [pageOrder]);

  // Track unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    const teamNameChanged = teamName !== (user?.team_name || '');
    const menuColorChanged = menuColor !== (user?.menu_color || DEFAULT_MENU_COLOR);
    const buttonColorChanged = buttonColor !== (user?.button_color || DEFAULT_BUTTON_COLOR);
    const hiddenPagesChanged = hiddenPagesJson !== userHiddenPagesJson;
    const pageOrderChanged = pageOrderJson !== userPageOrderJson;
    const securityEmailsChanged = securityEmailsEnabled !== (user?.security_emails_enabled !== false);
    const sidebarBgChanged = sidebarBgFile !== null || sidebarBgImage !== (user?.sidebar_bg_image || '');
    const sidebarOpacityChanged = sidebarBgOpacity !== (user?.sidebar_bg_opacity ?? 0.3);
    const sidebarOffsetChanged = sidebarBgOffset !== (user?.sidebar_bg_offset ?? 0);
    const showOpponentBadgesChanged = showOpponentBadges !== (user?.show_opponent_badges !== false);
    const countdownTimerChanged = countdownTimerEnabled !== (user?.countdown_timer_enabled !== false);
    
    return teamNameChanged || menuColorChanged || buttonColorChanged || hiddenPagesChanged || pageOrderChanged || securityEmailsChanged || sidebarBgChanged || sidebarOpacityChanged || sidebarOffsetChanged || showOpponentBadgesChanged || countdownTimerChanged;
  }, [teamName, menuColor, buttonColor, hiddenPagesJson, pageOrderJson, userHiddenPagesJson, userPageOrderJson, securityEmailsEnabled, sidebarBgFile, sidebarBgImage, sidebarBgOpacity, sidebarBgOffset, showOpponentBadges, countdownTimerEnabled, user?.team_name, user?.menu_color, user?.button_color, user?.security_emails_enabled, user?.sidebar_bg_image, user?.sidebar_bg_opacity, user?.sidebar_bg_offset, user?.show_opponent_badges, user?.countdown_timer_enabled]);

  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChanges(hasUnsavedChanges);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const togglePageVisibility = (pageId) => {
    if (hiddenPages.includes(pageId)) {
      setHiddenPages(hiddenPages.filter(id => id !== pageId));
    } else {
      setHiddenPages([...hiddenPages, pageId]);
    }
  };

  const uploadFile = async (file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API}/upload-image`, formData, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data.image_url;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let finalClubBadge = clubBadge;
      let finalHeaderImage = headerImage;
      let finalDressingRoomHeader = dressingRoomHeader;
      let finalSidebarBgImage = sidebarBgImage;
      let finalProfilePhoto = profilePhoto;
      
      // Upload files if selected
      if (badgeFile || headerFile || dressingRoomHeaderFile || sidebarBgFile || profilePhotoFile) {
        setUploading(true);
        
        if (badgeFile) {
          finalClubBadge = await uploadFile(badgeFile);
          setClubBadge(finalClubBadge);
          setBadgeFile(null);
        }
        if (headerFile) {
          finalHeaderImage = await uploadFile(headerFile);
          setHeaderImage(finalHeaderImage);
          setHeaderFile(null);
        }
        if (dressingRoomHeaderFile) {
          finalDressingRoomHeader = await uploadFile(dressingRoomHeaderFile);
          setDressingRoomHeader(finalDressingRoomHeader);
          setDressingRoomHeaderFile(null);
        }
        if (sidebarBgFile) {
          const uploadedUrl = await uploadFile(sidebarBgFile);
          setSidebarBgImage(uploadedUrl);
          setSidebarBgPreview(uploadedUrl);
          setSidebarBgFile(null);
          finalSidebarBgImage = uploadedUrl;
        }
        if (profilePhotoFile) {
          finalProfilePhoto = await uploadFile(profilePhotoFile);
          setProfilePhoto(finalProfilePhoto);
          setProfilePhotoPreview(finalProfilePhoto);
          setProfilePhotoFile(null);
        }
        
        setUploading(false);
      }
      
      const displayNameValue = displayNameRef.current?.value || null;
      const personalBioValue = personalBioRef.current?.value || null;
      const coachQualificationsValue = coachQualificationsRef.current?.value || null;
      const response = await axios.put(`${API}/settings`, {
        team_name: teamName || null,
        club_badge: finalClubBadge || null,
        header_image: finalHeaderImage || null,
        dressing_room_header_image: finalDressingRoomHeader || null,
        ticker_enabled: tickerEnabled,
        ticker_mode: tickerMode,
        ticker_label: tickerLabel || 'BREAKING NEWS:',
        ticker_text: tickerText || null,
        current_role: currentRole || null,
        profile_photo: finalProfilePhoto || null,
        menu_color: menuColor || DEFAULT_MENU_COLOR,
        button_color: buttonColor || DEFAULT_BUTTON_COLOR,
        display_name: displayNameValue,
        personal_bio: personalBioValue,
        coach_qualifications: coachQualificationsValue,
        hidden_pages: hiddenPages,
        page_order: pageOrder,
        security_emails_enabled: securityEmailsEnabled,
        timeline_auto_stats: timelineAutoStats,
        preferred_formation: preferredFormation || null,
        default_kick_off_time: defaultKickOffTime || null,
        half_length: halfLength || null,
        extra_time_half_length: extraTimeHalfLength || null,
        preset_non_selection_reasons: presetReasons,
        match_day_mode_enabled: matchDayModeEnabled,
        voice_button_position: voiceButtonPosition,
        voice_control_enabled: voiceControlEnabled,
        voice_engine: voiceEngine,
        show_opponent_badges: showOpponentBadges,
        countdown_timer_enabled: countdownTimerEnabled,
        coach_note_categories: noteCategories,
        cup_progress_labels: cupProgressLabels || {},
        player_card_stats: playerCardStats || [],
        drill_presets: drillPresets.length ? drillPresets : null,
        sidebar_bg_image: finalSidebarBgImage !== undefined ? finalSidebarBgImage : null,
        sidebar_bg_opacity: sidebarBgOpacity,
        sidebar_bg_offset: sidebarBgOffset
      });
      updateUser(response.data);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <UnsavedChangesDialog
        open={showPrompt}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
      <div className="space-y-6 page-slide-in max-w-2xl" data-testid="settings-container">
        {/* Header */}
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">Settings</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Customize your team profile and app appearance.</p>
        </div>

        {/* Account Info Section */}
        <AccountInfoSection
          user={user}
          displayNameRef={displayNameRef}
          personalBioRef={personalBioRef}
          coachQualificationsRef={coachQualificationsRef}
          currentRole={currentRole}
          setCurrentRole={setCurrentRole}
          profilePhotoPreview={profilePhotoPreview}
          profilePhotoFile={profilePhotoFile}
          onProfilePhotoChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              if (file.size > 5 * 1024 * 1024) {
                toast.error('Profile photo must be under 5MB');
                return;
              }
              setProfilePhotoFile(file);
              setProfilePhotoPreview(URL.createObjectURL(file));
            }
          }}
          onProfilePhotoRemove={() => {
            setProfilePhotoFile(null);
            setProfilePhoto('');
            setProfilePhotoPreview('');
          }}
          onLogout={handleLogout}
        />

        {/* Public Coach Profile toggle — enable/disable the /coach/:slug
            share link. Lives with Account Info because it's tied to the
            coach's personal identity (bio, qualifications, career stats),
            not to any single season's team identity. */}
        <PublicProfileSection user={user} onUpdate={updateUser} />

        {/* Team Identity Section — only shown when a season is active.
            When no season is selected, coach-personal fields (name, photo,
            current role, bio, qualifications) live in Account Settings above
            and team identity has no separate concept until a season exists. */}
        {user?.current_season_id ? (
          <TeamIdentitySection
            teamName={teamName}
            setTeamName={setTeamName}
            badgePreview={badgePreview}
            setBadgePreview={setBadgePreview}
            badgeFile={badgeFile}
            setBadgeFile={setBadgeFile}
            headerPreview={headerPreview}
            setHeaderPreview={setHeaderPreview}
            headerFile={headerFile}
            setHeaderFile={setHeaderFile}
            dressingRoomHeaderPreview={dressingRoomHeaderPreview}
            setDressingRoomHeaderPreview={setDressingRoomHeaderPreview}
            dressingRoomHeaderFile={dressingRoomHeaderFile}
            setDressingRoomHeaderFile={setDressingRoomHeaderFile}
            setDressingRoomHeader={setDressingRoomHeader}
            tickerEnabled={tickerEnabled}
            setTickerEnabled={setTickerEnabled}
            tickerMode={tickerMode}
            setTickerMode={setTickerMode}
            tickerLabel={tickerLabel}
            setTickerLabel={setTickerLabel}
            tickerText={tickerText}
            setTickerText={setTickerText}
            setClubBadge={setClubBadge}
            setHeaderImage={setHeaderImage}
            currentSeasonName={currentSeasonName}
          />
        ) : (
          <div
            className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3"
            data-testid="team-identity-no-season-placeholder"
          >
            <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 text-lg font-bold">!</div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-900">Team Identity is per-season</p>
              <p className="text-xs text-amber-800/85 mt-1">
                Coaches move clubs and teams — each season now carries its own team name, badge, headers, ticker and match defaults. Create a season to unlock these fields.
              </p>
              <a
                href="/seasons"
                className="inline-block mt-2 text-xs font-bold text-emerald-700 hover:text-emerald-800 underline"
                data-testid="team-identity-create-season-cta"
              >
                Create a season →
              </a>
            </div>
          </div>
        )}

        {/* Appearance Section */}
        <AppearanceSection
          menuColor={menuColor}
          setMenuColor={setMenuColor}
          buttonColor={buttonColor}
          setButtonColor={setButtonColor}
          sidebarBgImage={sidebarBgImage}
          sidebarBgOpacity={sidebarBgOpacity}
          setSidebarBgOpacity={setSidebarBgOpacity}
          sidebarBgFile={sidebarBgFile}
          setSidebarBgFile={setSidebarBgFile}
          sidebarBgPreview={sidebarBgPreview}
          setSidebarBgPreview={setSidebarBgPreview}
          setSidebarBgImage={setSidebarBgImage}
          sidebarBgOffset={sidebarBgOffset}
          setSidebarBgOffset={setSidebarBgOffset}
        />

        {/* Navigation Section */}
        <NavigationSection
          pageOrder={pageOrder}
          setPageOrder={setPageOrder}
          hiddenPages={hiddenPages}
          togglePageVisibility={togglePageVisibility}
          draggedPage={draggedPage}
          setDraggedPage={setDraggedPage}
          dragOverPage={dragOverPage}
          setDragOverPage={setDragOverPage}
        />

        {/* Subscription Management */}
        <SubscriptionSection />

        {/* Testimonial Loyalty Scheme — Phase 2 coach-facing widget */}
        <LoyaltyWidget />

        {/* Referral Program (LTD holders only) */}
        {user?.subscription_plan === 'lifetime' && (
          <ReferralSection user={user} />
        )}

        {/* Security Section */}
        <SecuritySection
          securityEmailsEnabled={securityEmailsEnabled}
          setSecurityEmailsEnabled={setSecurityEmailsEnabled}
        />

        {/* Match Defaults Section */}
        <MatchDefaultsSection
          preferredFormation={preferredFormation}
          setPreferredFormation={setPreferredFormation}
          defaultKickOffTime={defaultKickOffTime}
          setDefaultKickOffTime={setDefaultKickOffTime}
          halfLength={halfLength}
          setHalfLength={setHalfLength}
          extraTimeHalfLength={extraTimeHalfLength}
          setExtraTimeHalfLength={setExtraTimeHalfLength}
          presetReasons={presetReasons}
          setPresetReasons={setPresetReasons}
          countdownTimerEnabled={countdownTimerEnabled}
          setCountdownTimerEnabled={setCountdownTimerEnabled}
          noteCategories={noteCategories}
          setNoteCategories={setNoteCategories}
        />

        {/* Match Stats Section */}
        <MatchStatsSection
          timelineAutoStats={timelineAutoStats}
          setTimelineAutoStats={setTimelineAutoStats}
        />

        {/* Match Day Mode Section */}
        <MatchDayModeSection
          matchDayModeEnabled={matchDayModeEnabled}
          setMatchDayModeEnabled={setMatchDayModeEnabled}
          voiceButtonPosition={voiceButtonPosition}
          setVoiceButtonPosition={setVoiceButtonPosition}
          voiceControlEnabled={voiceControlEnabled}
          setVoiceControlEnabled={setVoiceControlEnabled}
          voiceEngine={voiceEngine}
          setVoiceEngine={setVoiceEngine}
        />

        {/* Drill Presets Section */}
        <DrillPresetsSection
          drillPresets={drillPresets}
          setDrillPresets={setDrillPresets}
        />

        {/* Opponents & Competitions Section */}
        <OpponentsCompetitionsSection
          showOpponentBadges={showOpponentBadges}
          setShowOpponentBadges={setShowOpponentBadges}
        />

        {/* Cup Progress Labels — coach-customisable badge + footer wording */}
        <CupProgressLabelsSection
          cupProgressLabels={cupProgressLabels}
          setCupProgressLabels={setCupProgressLabels}
        />

        {/* Player Card Stats — coach picks which stats appear and in what order */}
        <PlayerCardStatsSection
          playerCardStats={playerCardStats}
          setPlayerCardStats={setPlayerCardStats}
        />

        {/* Sessions Section */}
        <SessionsSection />

        {/* Data & Privacy (GDPR export + account deletion) */}
        <DataPrivacySection />

        {/* Preview Section */}
        <PreviewSection
          menuColor={menuColor}
          badgePreview={badgePreview}
          teamName={teamName}
          setBadgePreview={setBadgePreview}
        />

        {/* Save Button */}
        <div ref={saveButtonRef}>
          <Button
            onClick={handleSave}
            disabled={saving || uploading}
            className="w-full btn-primary rounded-xl h-12"
            data-testid="save-settings-btn"
          >
            <Save className="w-5 h-5 mr-2" strokeWidth={1.5} />
            {uploading ? 'Uploading...' : saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Floating Save Button */}
      {showFloatingButton && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white via-white to-transparent z-50" style={{ marginLeft: 'var(--sidebar-width, 0px)' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Button
              onClick={handleSave}
              disabled={saving || uploading}
              className="w-full btn-primary rounded-xl h-12 shadow-lg"
              data-testid="floating-save-btn"
            >
              <Save className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {uploading ? 'Uploading...' : saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}
